const DEFAULT_MODEL = "qwen3-vl-flash";
const DEFAULT_TIMEOUT_MS = 120000;

const cleanText = (value) => (value == null ? "" : String(value).trim());
const cleanNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const normalized = String(value).replace(/[^0-9,.-]/g, "").replace(/,/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};
const normalizeDate = (value) => {
  const text = cleanText(value);
  if (!text) return "";
  const normalized = text.replace(/\//g, "-").replace(/T.*$/, "");
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;
  if (/^\d{4}-\d{2}$/.test(normalized)) return normalized;
  const match = normalized.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  return normalized;
};
const normalizeMoneda = (value) => {
  const text = cleanText(value).toUpperCase();
  if (text === "SOL" || text === "SOLES" || text === "PEN") return "PEN";
  if (text === "DOL" || text === "DOLAR" || text === "USD") return "USD";
  return text || "PEN";
};
const normalizeRuc = (value) => cleanText(value).replace(/[^0-9]/g, "");

export const extractJsonFromText = (text) => {
  const raw = cleanText(text);
  if (!raw) throw new Error("La respuesta de Qwen no contiene JSON válido");
  const normalized = raw.replace(/^```(?:json)?/i, "").replace(/```$/i, "").trim();
  const firstBrace = normalized.indexOf("{");
  const lastBrace = normalized.lastIndexOf("}");
  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    throw new Error("La respuesta de Qwen no contiene JSON válido");
  }
  const jsonText = normalized.slice(firstBrace, lastBrace + 1);
  return JSON.parse(jsonText);
};

export const normalizeExtractionPayload = (payload = {}) => {
  const normalized = {
    proveedor: {
      ruc: normalizeRuc(payload.proveedor?.ruc || ""),
      razonSocial: cleanText(payload.proveedor?.razonSocial || ""),
      direccion: cleanText(payload.proveedor?.direccion || ""),
    },
    comprobante: {
      tipo: cleanText(payload.comprobante?.tipo || "FACTURA").toUpperCase(),
      serie: cleanText(payload.comprobante?.serie || ""),
      numero: cleanText(payload.comprobante?.numero || ""),
      fechaEmision: normalizeDate(payload.comprobante?.fechaEmision || ""),
      fechaVencimiento: normalizeDate(payload.comprobante?.fechaVencimiento || payload.comprobante?.fechaEmision || ""),
      moneda: normalizeMoneda(payload.comprobante?.moneda || "PEN"),
      documentoModificado: cleanText(payload.comprobante?.documentoModificado || payload.comprobante?.facturaAfectada || payload.comprobante?.documentoAfectado || "").toUpperCase(),
      motivo: cleanText(payload.comprobante?.motivo || payload.comprobante?.sustento || payload.comprobante?.motivoAnulacion || ""),
    },
    montos: {
      subtotal: cleanNumber(payload.montos?.subtotal),
      igv: cleanNumber(payload.montos?.igv),
      total: cleanNumber(payload.montos?.total),
      descuentos: cleanNumber(payload.montos?.descuentos),
      otrosCargos: cleanNumber(payload.montos?.otrosCargos),
    },
    items: Array.isArray(payload.items) ? payload.items.map((item) => ({
      descripcion: cleanText(item.descripcion || ""),
      cantidad: cleanNumber(item.cantidad || 1),
      unidad: cleanText(item.unidad || "NIU"),
      precioUnitario: cleanNumber(item.precioUnitario),
      subtotal: cleanNumber(item.subtotal),
    })) : [],
    cliente: {
      ruc: normalizeRuc(payload.cliente?.ruc || ""),
      razonSocial: cleanText(payload.cliente?.razonSocial || ""),
    },
    metadatos: {
      fuente: "QWEN_VISION",
      confianza: Number(payload.confianza || payload.metadatos?.confianza || 70),
      fechaProcesamiento: payload.metadatos?.fechaProcesamiento || new Date().toISOString(),
    },
  };

  return normalized;
};

export class QwenVisionProvider {
  constructor({ callable, model = DEFAULT_MODEL, timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
    this.callable = callable;
    this.model = model;
    this.timeoutMs = timeoutMs;
  }

  async extractStructuredData({ images = [], prompt, context = {} } = {}) {
    if (!this.callable) {
      throw new Error("Qwen Vision no está configurado (callable no disponible)");
    }

    if (!Array.isArray(images) || images.length === 0) {
      throw new Error("La respuesta de Qwen no contiene JSON válido");
    }

    const requestPayload = {
      model: this.model,
      messages: [
        {
          role: "system",
          content: "Responde solo con JSON válido, sin markdown y sin explicaciones."
        },
        {
          role: "user",
          content: [
            { type: "text", text: prompt || "Extrae los datos de la factura en formato JSON." },
            ...images.map((image) => ({
              type: "image_url",
              image_url: { url: image.base64 ? `data:${image.mimeType || "image/png"};base64,${image.base64}` : image.url }
            }))
          ]
        }
      ],
      timeout_ms: this.timeoutMs,
      ...context,
    };

    const response = await this.callable(requestPayload);
    const responseText = response?.data?.choices?.[0]?.message?.content || response?.data?.text || response?.data || "";
    const parsed = typeof responseText === "string" ? extractJsonFromText(responseText) : responseText;
    return normalizeExtractionPayload(parsed);
  }
}

export default QwenVisionProvider;
